import { type Node, type XYPosition } from '@xyflow/react';
import sortBy from 'lodash/sortBy';

import { type DiagramSide } from '@/types/schema';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import {
  type DiagramBuilderEdge,
  type DiagramBuilderNode,
  type DiagramBuilderNodeStyle,
  type DiagramBuilderStage,
} from './diagram';
import {
  getStageIDAt,
  getStageLabel,
  getTileCenterY,
  type StageBoundary,
  type StageTile,
} from './diagramStages';

/*
 * Each edit returns a new diagram and does not change its input. The editor
 * gives the new diagram to its undo history as one step.
 */

function withContent(
  diagram: DiagramBuilder_DiagramFragment,
  changes: Partial<DiagramBuilder_DiagramFragment['content']>
): DiagramBuilder_DiagramFragment {
  return { ...diagram, content: { ...diagram.content, ...changes } };
}

/** The kind, text, and style of a node that the editor adds. */
export type NewDiagramNode = Pick<
  DiagramBuilderNode,
  'kind' | 'label' | 'nodeStyle'
>;

/**
 * Adds `node` with empty notes and no estate category at `position`. The new
 * node goes in `stageId`, or in the first stage when `stageId` is not set.
 */
export function addDiagramNode(
  diagram: DiagramBuilder_DiagramFragment,
  nodeID: string,
  position: XYPosition,
  node: NewDiagramNode,
  stageId?: string | null
): DiagramBuilder_DiagramFragment {
  const { content } = diagram;
  return withContent(diagram, {
    nodes: [
      ...content.nodes,
      {
        ...node,
        nodeID,
        detail: '',
        stageId: stageId ?? content.stages[0]?.id,
        position: { x: Math.round(position.x), y: Math.round(position.y) },
      },
    ],
  });
}

/** The two ends of a connection: a node and the side of the node. */
export interface DiagramConnectionEnds {
  sourceNodeID: string;
  targetNodeID: string;
  sourceSide: DiagramSide;
  targetSide: DiagramSide;
}

/**
 * Adds a connection without a label between the sides of two nodes. A
 * connection without a label shows no label.
 */
export function addDiagramConnection(
  diagram: DiagramBuilder_DiagramFragment,
  edgeID: string,
  ends: DiagramConnectionEnds
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    edges: [...diagram.content.edges, { edgeID, label: '', ...ends }],
  });
}

/**
 * Moves the ends of a connection to other nodes or sides. The manual bends
 * are valid only for the old ends, so the connection loses them.
 */
export function reconnectDiagramEdge(
  diagram: DiagramBuilder_DiagramFragment,
  edgeID: string,
  ends: DiagramConnectionEnds
): DiagramBuilder_DiagramFragment {
  return editDiagramEdge(diagram, edgeID, { ...ends, waypoints: null });
}

/** The tile fields that the tile shows as text. They set the tile height. */
const TILE_TEXT_FIELDS = ['label', 'subtitle', 'detail'] as const;

/**
 * Applies `changes` to the tile with this ID. When `changes` gives new text,
 * the tile also loses its saved height, so that it fits its new content.
 * Style changes keep the saved height.
 */
export function editDiagramNode(
  diagram: DiagramBuilder_DiagramFragment,
  id: string,
  changes: Partial<DiagramBuilderNode>
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    nodes: diagram.content.nodes.map((node) => {
      if (node.nodeID !== id) return node;
      // A missing subtitle can be undefined or null. Both are the same text.
      const textChanged = TILE_TEXT_FIELDS.some(
        (field) =>
          field in changes && (changes[field] ?? null) !== (node[field] ?? null)
      );
      const next = { ...node, ...changes };
      if (!textChanged) return next;
      return { ...next, nodeStyle: { ...next.nodeStyle, height: null } };
    }),
  });
}

/** Applies `changes` to the connection with this ID. */
export function editDiagramEdge(
  diagram: DiagramBuilder_DiagramFragment,
  id: string,
  changes: Partial<DiagramBuilderEdge>
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    edges: diagram.content.edges.map((edge) =>
      edge.edgeID === id ? { ...edge, ...changes } : edge
    ),
  });
}

/** Applies the style `changes` to each node in `ids`. */
export function editDiagramNodeStyles(
  diagram: DiagramBuilder_DiagramFragment,
  ids: ReadonlySet<string>,
  changes: Partial<DiagramBuilderNodeStyle>
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    nodes: diagram.content.nodes.map((node) =>
      ids.has(node.nodeID)
        ? { ...node, nodeStyle: { ...node.nodeStyle, ...changes } }
        : node
    ),
  });
}

/**
 * The style changes that show or hide the notes or the footer of a tile. A
 * shown field saves null, the contract default.
 */
export function getTextVisibilityStyle(
  field: 'notes' | 'footer',
  show: boolean
): Partial<DiagramBuilderNodeStyle> {
  const hide = show ? null : true;
  return field === 'notes' ? { hideDetail: hide } : { hideFooterLabel: hide };
}

/** Returns the saved divider of each stage after the first, in any order. */
function getSavedBoundaries(stages: DiagramBuilderStage[]): StageBoundary[] {
  return stages.slice(1).flatMap((stage) => {
    if (stage.dividerY == null) return [];
    const label = getStageLabel(stage);
    return [{ stageID: stage.id, label, y: stage.dividerY }];
  });
}

/** Returns the vertical center of each tile, by tile ID. */
function getCenters(tiles: StageTile[]) {
  return new Map(tiles.map((tile) => [tile.id, getTileCenterY(tile)]));
}

/** Returns a new stage without a divider. */
function newStage(id: string): DiagramBuilderStage {
  return {
    __typename: 'DiagramStage',
    id,
    label: null,
    dividerY: null,
  };
}

/**
 * Sets the stages of `diagram`, and puts each tile in `tiles` in the stage at
 * its vertical center. Only the divider heights decide the stages, so the
 * order of the later stages in `stages` does not matter. The saved stages
 * are in divider height order, as the save contract requires. A node without
 * a canvas tile keeps its stage, or goes to the first stage when its stage
 * is gone.
 */
function withStages(
  diagram: DiagramBuilder_DiagramFragment,
  [first, ...later]: DiagramBuilderStage[],
  tiles: StageTile[]
): DiagramBuilder_DiagramFragment {
  if (!first) return withContent(diagram, { stages: [] });
  const stages = [first, ...sortBy(later, (stage) => stage.dividerY)];
  const stageIDs = new Set(stages.map((stage) => stage.id));
  const boundaries = getSavedBoundaries(stages);
  const centers = getCenters(tiles);
  return withContent(diagram, {
    stages,
    nodes: diagram.content.nodes.map((node) => {
      const center = centers.get(node.nodeID);
      if (center !== undefined)
        return { ...node, stageId: getStageIDAt(stages, boundaries, center) };
      if (node.stageId && stageIDs.has(node.stageId)) return node;
      return { ...node, stageId: first.id };
    }),
  });
}

/**
 * Sets the label of the divider before the stage with this ID. The divider
 * height does not change.
 */
export function editDiagramStageLabel(
  diagram: DiagramBuilder_DiagramFragment,
  stageID: string,
  label: string
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    stages: diagram.content.stages.map((stage, index) => {
      if (index === 0 || stage.id !== stageID) return stage;
      return { ...stage, label };
    }),
  });
}

/**
 * Adds a divider at canvas height `y`, with a blank label, for a new stage
 * with ID `stageIDs.next`. The tiles with a vertical center at or below `y`,
 * up to the next divider, go to the new stage. A diagram without stages
 * first gets one stage, with ID `stageIDs.first`, for all its tiles. `y` can
 * be anywhere, also on an empty canvas or outside the tiles.
 *
 * `tiles` gives the canvas box of each tile. Save the divider heights before
 * this edit.
 */
export function addDiagramStageBoundary(
  diagram: DiagramBuilder_DiagramFragment,
  stageIDs: { first: string; next: string },
  y: number,
  tiles: StageTile[]
): DiagramBuilder_DiagramFragment {
  const { stages } = diagram.content;
  return withStages(
    diagram,
    [
      ...(stages.length ? stages : [newStage(stageIDs.first)]),
      { ...newStage(stageIDs.next), label: '', dividerY: y },
    ],
    tiles
  );
}

/**
 * Moves the divider before the stage with this ID to canvas height `y`. The
 * tiles do not move. Each tile goes to the stage at its vertical center. The
 * divider can pass other dividers.
 */
export function moveDiagramStageBoundary(
  diagram: DiagramBuilder_DiagramFragment,
  stageID: string,
  y: number,
  tiles: StageTile[]
): DiagramBuilder_DiagramFragment {
  const { stages } = diagram.content;
  if (stages.findIndex((stage) => stage.id === stageID) < 1) return diagram;
  return withStages(
    diagram,
    stages.map((stage) =>
      stage.id === stageID ? { ...stage, dividerY: y } : stage
    ),
    tiles
  );
}

/**
 * Removes the divider before the stage with this ID, and the stage. Each tile
 * goes to the stage at its vertical center, so the tiles of the removed stage
 * go to the stage above the divider.
 */
export function removeDiagramStageBoundary(
  diagram: DiagramBuilder_DiagramFragment,
  stageID: string,
  tiles: StageTile[]
): DiagramBuilder_DiagramFragment {
  const { stages } = diagram.content;
  if (stages.findIndex((stage) => stage.id === stageID) < 1) return diagram;
  return withStages(
    diagram,
    stages.filter((stage) => stage.id !== stageID),
    tiles
  );
}

/**
 * Removes the saved position of each tile and the manual bends of each
 * connection, so that the automatic layout places and routes all of them.
 */
export function resetDiagramPositions(
  diagram: DiagramBuilder_DiagramFragment
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    nodes: diagram.content.nodes.map((node) => ({ ...node, position: null })),
    edges: diagram.content.edges.map((edge) => ({ ...edge, waypoints: null })),
  });
}

/**
 * Saves the position of each tile in `tiles` after the user drags or resizes
 * `tile`. `tile` gives its own position, and its width and height when it has
 * them. React Flow sets them after a resize. The saved width and height are
 * whole numbers of pixels. The other tiles keep their current positions, so
 * the automatic layout does not move them later.
 *
 * The manual bends of a connection are valid only for the positions of its
 * tiles, so the connections of `tile` lose their bends.
 *
 * Each tile in `movedIDs` goes to the stage at its vertical center, by the
 * saved divider heights. The other stages and tiles do not move.
 */
export function placeDiagramNode(
  diagram: DiagramBuilder_DiagramFragment,
  tile: Pick<Node, 'id' | 'position' | 'width' | 'height' | 'measured'>,
  tiles: StageTile[],
  movedIDs: ReadonlySet<string> = new Set()
): DiagramBuilder_DiagramFragment {
  const positions = new Map(tiles.map((node) => [node.id, node.position]));
  positions.set(tile.id, tile.position);
  const centers = getCenters([...tiles, tile]);
  const { stages } = diagram.content;
  const boundaries = getSavedBoundaries(stages);
  const stageAtCenter = (node: DiagramBuilderNode) => {
    const center = centers.get(node.nodeID);
    if (!stages.length || !movedIDs.has(node.nodeID) || center === undefined)
      return node.stageId;
    return getStageIDAt(stages, boundaries, center);
  };
  return withContent(diagram, {
    edges: diagram.content.edges.map((edge) =>
      edge.sourceNodeID === tile.id || edge.targetNodeID === tile.id
        ? { ...edge, waypoints: null }
        : edge
    ),
    nodes: diagram.content.nodes.map((node) => {
      const position = positions.get(node.nodeID) ?? node.position;
      const stageId = stageAtCenter(node);
      if (node.nodeID !== tile.id) return { ...node, position, stageId };
      return {
        ...node,
        position,
        stageId,
        nodeStyle: {
          ...node.nodeStyle,
          ...(tile.width && { width: Math.round(tile.width) }),
          ...(tile.height && { height: Math.round(tile.height) }),
        },
      };
    }),
  });
}

/**
 * Saves the current canvas position of each tile without a saved position,
 * and the height and label of each divider in `boundaries`. Call it before
 * an edit that changes the automatic layout input, such as a new connection,
 * so that the automatic layout does not move tiles that the user sees in
 * place. A stage edit needs the saved divider heights, so this also saves
 * the divider heights of the automatic layout.
 */
export function pinDiagramPositions(
  diagram: DiagramBuilder_DiagramFragment,
  tiles: Pick<Node, 'id' | 'position'>[],
  boundaries: StageBoundary[] = []
): DiagramBuilder_DiagramFragment {
  const positions = new Map(tiles.map((node) => [node.id, node.position]));
  const boundaryByStageID = new Map(
    boundaries.map((boundary) => [boundary.stageID, boundary])
  );
  return withContent(diagram, {
    stages: diagram.content.stages.map((stage, index) => {
      const boundary = boundaryByStageID.get(stage.id);
      if (index === 0 || !boundary) return stage;
      return { ...stage, label: boundary.label, dividerY: boundary.y };
    }),
    nodes: diagram.content.nodes.map((node) => {
      const position = positions.get(node.nodeID);
      if (node.position || !position) return node;
      return {
        ...node,
        position: { x: Math.round(position.x), y: Math.round(position.y) },
      };
    }),
  });
}

/** Remove a tile or connection and its dependent edges as one undoable edit. */
export function removeDiagramElement(
  diagram: DiagramBuilder_DiagramFragment,
  id: string
): DiagramBuilder_DiagramFragment {
  const { content } = diagram;
  const removed = new Set([
    id,
    ...content.edges
      .filter((edge) => edge.sourceNodeID === id || edge.targetNodeID === id)
      .map((edge) => edge.edgeID),
  ]);
  return withContent(diagram, {
    nodes: content.nodes.filter((node) => !removed.has(node.nodeID)),
    edges: content.edges.filter((edge) => !removed.has(edge.edgeID)),
  });
}
